import * as React from "react";
import { useLingui } from "@lingui/react/macro";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";

import { getWorkspaceTabDef } from "@/features/workspace/config";
import {
  selectCurrentTab,
  useWorkspaceStore,
} from "@/features/workspace-router/state/workspaceStore";

import { useHasDiffForDoc } from "@/features/sources/state/diffStore";

import { useActiveAction } from "../hooks/useActiveAction";
import { FocusedActionView } from "./FocusedActionView";
import {
  ScalarActionProposalMount,
  isScalarSurfaceActionType,
} from "./ScalarActionProposalMount";
import {
  VoiceReportGenerateCard,
  VOICE_REPORT_GENERATE_ACTION_TYPE,
} from "./voice_report_generate/VoiceReportGenerateCard";

/**
 * FocusedActionSurface — workspace-aware mount for the focused HITL
 * proposal view.
 *
 * Reads the live ``?proposal=<id>`` search param off the router and,
 * when present, mounts ``<FocusedActionView>`` over the current
 * workspace hub. The component is rendered ONCE inside the authed
 * shell (next to ``<Outlet>``) so we don't need to thread it through
 * every per-tab route file — single mount point per CLAUDE.md DRY.
 *
 * Visual hierarchy: when ``?proposal=`` is set, this surface returns
 * the focused view directly (taking the full work area), and the
 * caller hides ``<Outlet>`` for the duration. When the param is
 * absent, the surface renders ``null`` and the routed hub keeps the
 * full viewport.
 *
 * Edge cases:
 *   - Proposal id present but not in the store (e.g. user reloaded
 *     after the proposal was dismissed): we render ``null`` and let
 *     the URL fall through. The next mount of ``ActionFormSurface``
 *     pop-up chip will reset the param when the user picks a fresh
 *     proposal.
 *   - Tab change while a proposal is open: TanStack Router clears the
 *     search params on navigate-to-a-different-route, so the focused
 *     view detaches automatically. The action survives in the store
 *     (no remove call) so the user can re-open it from the chip.
 */
export const FocusedActionSurface: React.FC = () => {
  const { t } = useLingui();
  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = useWorkspaceStore(selectCurrentTab);

  // Read the live ``?proposal=`` param from TanStack location.search.
  // ``useLocation`` already triggers a re-render on URL change so we
  // don't need a separate subscription.
  const search = location.search as { proposal?: unknown; msg?: unknown };
  const proposalId =
    typeof search.proposal === "string" && search.proposal
      ? search.proposal
      : null;
  const messageId =
    typeof search.msg === "string" && search.msg ? search.msg : null;

  const action = useActiveAction(proposalId ?? undefined);

  // ── Single-review-surface guard (BUG-3, June 2026) ─────────────────────
  //
  // Document/deck EDIT proposals are reviewed IN-SLIDE through the
  // ``doc_diff_payload`` redline rail (``diffStore`` → ``ArtifactPane`` /
  // ``AISummaryPanel``). When that envelope is present for the targeted
  // doc, this separate ``action_proposal`` overlay would be a SECOND
  // acceptance surface — clicking it also accepts, so the user sees a
  // duplicate card. We suppress the overlay (and clear the lingering
  // ``?proposal=`` param) so the in-slide rail is the only review surface.
  //
  // ``hasDiffForDoc`` is a primitive selector (no infinite-loop risk) and
  // is ``false`` for non-document proposals (no ``documentEdit.documentId``),
  // so email / calendar / vendor proposals keep their overlay untouched.
  const documentId = action?.documentEdit?.documentId;
  // Resilient to the doc_id FORM the diff envelope is keyed under: the rail may
  // arrive keyed by the fork's ``weaviate_doc_id`` (``fork_…``) while the action
  // carries a different form — so we fall back to matching by ``proposalId``
  // (June 18, 2026). Without this the suppression silently misses on a key-form
  // mismatch and the overlay double-surfaces.
  const hasDiffForDoc = useHasDiffForDoc(documentId, proposalId);
  // ``document_fork_edit`` now ALSO defers to the in-slide redline rail (June 18,
  // 2026): the fork blob is in MinIO at proposal-time so the editor opens it
  // immediately. The old fork exclusion here produced the DOUBLE surface (empty
  // overlay + rail). Forks are treated exactly like ``document_edit``: when a
  // diff payload exists, the in-slide rail is the single review surface.
  const diffOwnsReview = hasDiffForDoc;

  // Self-heal: if the URL carries a stale ``?proposal=<id>`` but the
  // store no longer has the action (dismissed elsewhere, server-side
  // expire, refresh after a reject), strip the param so the user is
  // returned to the hub instead of staring at a blank overlay. Effect
  // runs only when the (proposalId, action) tuple has a mismatch.
  //
  // Same clean-up fires when a diff rail owns the review for a doc-edit
  // proposal (``hasDiffForDoc``) — drop ``?proposal=`` so the URL reflects
  // the single in-slide review surface and a back-nav doesn't re-open the
  // suppressed card.
  React.useEffect(() => {
    if (proposalId && (!action || diffOwnsReview)) {
      void navigate({
        to: router.state.location.pathname,
        search: (prev) => {
          const { proposal: _drop, ...rest } = prev as Record<string, unknown>;
          return rest;
        },
      });
    }
  }, [proposalId, action, diffOwnsReview, navigate, router]);

  const handleBack = React.useCallback(() => {
    // Clear ``?proposal=`` while preserving other params. TanStack
    // ``navigate`` with a callback receives the current search and
    // returns the new shape; spreading and dropping ``proposal`` is
    // safer than passing ``{}`` (would also wipe ``focus`` / ``msg`` /
    // ``sub`` if the user had multiple params).
    void navigate({
      to: router.state.location.pathname,
      search: (prev) => {
        const { proposal: _drop, ...rest } = prev as Record<string, unknown>;
        return rest;
      },
    });
  }, [navigate, router]);

  // Suppress the overlay entirely when the in-slide redline rail owns the
  // review for this doc-edit proposal (the effect above also drops the
  // ``?proposal=`` param) — one review surface, no duplicate card.
  if (!proposalId || !action || diffOwnsReview) return null;

  // Eyebrow label — read from the workspace tab def so the focused view
  // surfaces the same "CONVERSAZIONI" / "CALENDARIO" eyebrow used by
  // the sibling focused panes. Falls back to a generic "Proposta" copy
  // when the tab def is missing (defensive — should never happen in
  // practice since the action is already filtered for the current tab
  // via ``resolveTabForActionType``).
  const tabDef = getWorkspaceTabDef(currentTab);
  const tabLabel = tabDef?.label ?? t`Proposta`;

  // Phase 4 (May 2026) — a freshly-proposed action TAKES OVER the work area
  // through the generalised ``ActionProposalSurface`` (the same shell the
  // Wave 9 presentation deck uses), instead of the legacy
  // ``FocusedViewLayout`` + ``ActionDrawer`` footer chrome. Migration is
  // opt-in per action type via ``isScalarSurfaceActionType`` so no existing
  // flow half-breaks: types not yet routed through the unified surface keep
  // the ``FocusedActionView`` overlay below.
  const useUnifiedSurface = isScalarSurfaceActionType(action.actionType);
  // Per-type cards that ship their OWN bespoke editor on top of the shared
  // ``ActionProposalSurface`` shell. ``voice_report_generate`` is the first
  // one (Workstream B M2.5, June 17, 2026) — it has no vendor preview but
  // its 5-field schema benefits from a colocated component (dataset picker
  // FIRST + diff tracking ribbon). Add a new entry here to onboard the
  // next bespoke card without touching the scalar / legacy branches.
  const useVoiceReportCard = action.actionType === VOICE_REPORT_GENERATE_ACTION_TYPE;

  // Absolute-positioned overlay so the underlying ``<Outlet>`` keeps
  // its DOM (scroll position, filters, infinite-scroll cursor) intact.
  // ``bg-cream/95`` matches the workspace pane background so the
  // viewer reads as the active surface (not as a floating modal).
  return (
    <div
      // z-40: bumped from z-10 (May 24, 2026) — Eigenpal docx editor
      // portals its toolbar at a higher stacking context than the prior
      // z-10, which obscured this overlay's Confirm CTA when both the
      // proposal AND a doc artifact were open at the same time. z-40
      // sits above editor chrome but below modal dialogs (z-50+).
      className="absolute inset-0 z-40 flex h-full flex-col bg-cream/95 backdrop-blur-sm"
      role="region"
      aria-label={t`Proposta in dettaglio`}
    >
      {useVoiceReportCard ? (
        <VoiceReportGenerateCard action={action} onAfterReject={handleBack} />
      ) : useUnifiedSurface ? (
        <ScalarActionProposalMount action={action} onAfterReject={handleBack} />
      ) : (
        <FocusedActionView
          action={action}
          tabLabel={tabLabel}
          messageId={messageId}
          onBack={handleBack}
        />
      )}
    </div>
  );
};
FocusedActionSurface.displayName = "FocusedActionSurface";
